import { FC } from "react";
import styles from "./IndicatorBar.module.scss";
import { classNames } from "@/lib/utils";
import { useAtomValue } from "jotai";
import { themeState, positiveState, negativeState, neutralState, blackState, whiteState } from "@/lib/store";
import { alpha } from "@/lib/ortex-charts/color";

interface PropsTypes {
    bigFont?: boolean;
    middle?: number;
    noPointer?: boolean;
    percentage: number;
    textAlign?: "start" | "end" | "left" | "right" | "center" | "justify" | "match-parent";
    title?: string;
    titleWeight?: "200" | "300" | "400" | "500" | "600" | "700" | "800" | "900";
    titleFont?: string;
    type?: "linear" | "profit-loss";
}

const IndicatorBar: FC<PropsTypes> = ({ bigFont, middle, noPointer, percentage, textAlign = "left", title, type = "linear", titleWeight = "400", titleFont = "11px" }) => {
    const theme = useAtomValue(themeState);
    const positive = useAtomValue(positiveState);
    const positiveLighter = alpha(positive, 0.5);
    const negative = useAtomValue(negativeState);
    const negativeLighter = alpha(negative, 0.5);
    const neutral = useAtomValue(neutralState);
    const black = useAtomValue(blackState);
    const white = useAtomValue(whiteState);

    const position = !isFinite(percentage) ? "50%" : percentage < 0 ? "0%" : percentage > 100 ? "100%" : percentage + "%";
    middle = isFinite(middle) ? middle : 50;

    const backgroundGradient =
        type === "linear"
            ? `linear-gradient(to right, ${negative}, ${neutral}, ${positive})`
            : `linear-gradient(to right, ${negative} 0%, ${negativeLighter} ${Math.max(middle - 1, 0)}%, ${theme === "dark" ? black : white} ${middle}%, ${positiveLighter} ${Math.min(middle + 1, 100)}%, ${positive} 100%)`;

    return (
        <div className={classNames(styles.IndicatorBar, styles[bigFont && "big-font"])}>
            <p style={{ textAlign, fontWeight: titleWeight, fontSize: titleFont, textTransform: "initial" }}>{title}</p>
            <div className={styles.container}>
                <div className={styles.gradient} style={{ backgroundImage: backgroundGradient }}></div>
                {!noPointer && (
                    <div className={classNames(styles[theme], styles.pointer)} style={{ left: `${position}` }}>
                        <div className={styles.bar}></div>
                        <div className={styles.bottom}></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IndicatorBar;
